<template>
  <div v-if="hasData">
    <div ref="chartEl" class="w-full h-96"></div>
  </div>
  <div v-else class="flex h-32 justify-center items-center">
    <p class="text-muted-foreground">{{ $t("report.sales_distribution.no_data") }}</p>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount, nextTick } from 'vue'
import * as echarts from 'echarts'
import type { ProductsReportResponse } from '../../responses'
import { useAppSettingsStore } from '../../stores/AppSettingsStore'
import { formatCurrencyForCenter } from '../../utils/Languages'
import { colorsUtils } from '../../utils/ColorsUtils'
import { useIsDarkMode } from '../../composables/useIsDarkMode'

const props = defineProps<{
  productsReport: ProductsReportResponse
  centerId: number
}>()

const appSettingsStore = useAppSettingsStore()

const chartEl = ref<HTMLElement>()
let chartInstance: echarts.ECharts | null = null

const isDark = useIsDarkMode()

const revenueDistribution = computed(() => {
  return props.productsReport?.performance_analysis?.diversity_metrics?.revenue_distribution ?? null
})

const hasData = computed(() => {
  return revenueDistribution.value != null && Object.keys(revenueDistribution.value).length > 0
})

// Colors are resolved per family name for cross-chart consistency

// --- Chart options ---
function buildChartOptions(): echarts.EChartsOption | null {
  const dist = revenueDistribution.value
  if (!dist) return null

  const families = Object.entries(dist).sort(([, a], [, b]) => b - a)
  const centerVal = appSettingsStore.getCenter(props.centerId)
  const totalRevenue = families.reduce((sum, [, revenue]) => sum + revenue, 0)

  // Group small percentages (< 3 %) into "Others"
  const threshold = 0.03
  const mainFamilies: [string, number][] = []
  let othersTotal = 0

  for (const [family, revenue] of families) {
    if (revenue / totalRevenue >= threshold) {
      mainFamilies.push([family, revenue])
    } else {
      othersTotal += revenue
    }
  }
  if (othersTotal > 0) {
    mainFamilies.push(['Others', othersTotal])
  }

  const seriesData = mainFamilies.map(([family, revenue]) => ({
    name: family,
    value: revenue,
    itemStyle: {
      color: colorsUtils.getColorForName(family),
    },
  }))

  const dark = isDark.value
  const textColor = dark ? 'rgba(255, 255, 255, 0.75)' : 'rgba(55, 65, 81, 0.9)'
  const tooltipBg = dark ? 'rgba(30, 31, 34, 0.95)' : 'rgba(255, 255, 255, 0.95)'
  const tooltipBorder = dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'
  const tooltipText = dark ? '#fff' : 'rgba(17, 24, 39, 0.9)'
  const segmentBorder = dark ? '#1B1C1E' : '#ffffff'

  return {
    textStyle: {
      fontSize: 11,
      fontFamily: "'Lato', system-ui, sans-serif",
    },
    tooltip: {
      trigger: 'item',
      backgroundColor: tooltipBg,
      borderColor: tooltipBorder,
      borderWidth: 1,
      borderRadius: 10,
      textStyle: {
        color: tooltipText,
        fontSize: 12,
      },
      extraCssText: 'box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);',
      formatter: (params: any) => {
        const percentage = ((params.value / totalRevenue) * 100).toFixed(1)
        const formattedValue = centerVal
          ? formatCurrencyForCenter(params.value, centerVal)
          : params.value
        return `
          <div style="font-weight: 600; margin-bottom: 4px;">${params.name}</div>
          <div style="display: flex; align-items: center; gap: 8px;">
            <div style="width: 8px; height: 8px; border-radius: 50%; background: ${params.color};"></div>
            <span>${formattedValue} (${percentage}%)</span>
          </div>
        `
      },
    },
    legend: {
      orient: 'horizontal',
      bottom: '5%',
      textStyle: {
        color: textColor,
        fontSize: 11,
        fontWeight: 500,
      },
      itemGap: 15,
      icon: 'circle',
      itemWidth: 8,
      itemHeight: 8,
    },
    series: [
      {
        name: 'Revenue Distribution',
        type: 'pie',
        radius: ['40%', '65%'],
        center: ['50%', '40%'],
        avoidLabelOverlap: true,
        itemStyle: {
          borderRadius: 4,
          borderColor: segmentBorder,
          borderWidth: 2,
        },
        label: {
          show: true,
          position: 'outside',
          formatter: '{b}\n{d}%',
          fontSize: 11,
          fontWeight: 500,
          color: textColor,
        },
        labelLine: {
          show: true,
          length: 15,
          length2: 10,
          smooth: 0.2,
        },
        emphasis: {
          scaleSize: 6,
          label: {
            fontSize: 12,
            fontWeight: 700,
          },
        },
        data: seriesData,
      },
    ],
  }
}

// --- Init / update / dispose ---
async function initializeChart() {
  let attempts = 0
  const maxAttempts = 10

  const tryInitialize = () => {
    if (!chartEl.value && attempts < maxAttempts) {
      attempts++
      setTimeout(tryInitialize, 100)
      return
    }
    if (!chartEl.value) return

    if (chartInstance) {
      chartInstance.dispose()
    }
    chartInstance = echarts.init(chartEl.value)
    updateChart()
  }

  await nextTick()
  tryInitialize()
}

function updateChart() {
  if (!chartInstance) return
  const options = buildChartOptions()
  if (options) {
    chartInstance.setOption(options, true)
  }
}

// Re-init when data changes and chart does not exist yet
watch(revenueDistribution, async (newData) => {
  if (newData && !chartInstance) {
    await initializeChart()
  } else if (chartInstance && newData) {
    updateChart()
  }
}, { deep: true })

watch(isDark, () => {
  if (chartInstance) updateChart()
})

// Handle visibility (parent may toggle v-if around this component)
watch(hasData, async (visible) => {
  if (visible) {
    await nextTick()
    setTimeout(async () => {
      await initializeChart()
    }, 300)
  }
}, { immediate: true })

onBeforeUnmount(() => {
  if (chartInstance) {
    chartInstance.dispose()
    chartInstance = null
  }
})
</script>
